import gsap from 'gsap';
import { motion } from 'framer-motion';
import { Box, HStack, IconButton, Image, Text, VStack } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppRoutes from 'config/AppRoutes';
import PageLayout from 'ui/layout/PageLayout';
import Button from 'ui/molecules/Button';
import RoundSlider from 'ui/molecules/RoundSlider';
import { ImArrowRight2, ImMinus, ImPlus } from 'react-icons/im';
import { useState, useCallback, useRef, useEffect } from 'react';
import { updateConsumption } from 'store/answers/answersSlice';
import { useDispatch, useSelector } from 'react-redux';
import {
  BusinessChoiceSelector,
  ConsumptionSelector,
  PanelSelector,
  RoofAngleSelector,
  RoofTypeSelector,
} from 'store/selectors';
import InputNumber from 'ui/molecules/InputNumber';
import VideoBlock from 'components/VideoBlock';
import getValidVideoAngle from 'features/utils/getValidVideoAngle';
import getLightAmountValue from 'utils/getLightAmountValue';
import { getEstimatedPanelsForConsumption } from 'utils/calculation/calculation';
import PanelIndicator from 'components/PanelIndicator';
import { formatNumber } from 'utils/formatNumber';
import { SMEContactReason } from 'pages/PageSMEContact/PageSMEContact';
import useStepNavigation from 'utils/hooks/useStepNavigation';
import useAsset, { AssetType } from 'utils/hooks/useAsset';
import usePanelList from '../PagePanel/hooks/usePanelList';
import useConsumptionConfig from './hooks/useConsumptionConfig';

const PageConsumption = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const currentRoute = AppRoutes.CONSUMPTION;
  const userConsumption = useSelector(ConsumptionSelector);
  const roofType = useSelector(RoofTypeSelector);
  const { navigateToNextStep, navigateToPreviousStep } =
    useStepNavigation(currentRoute);
  const panelList = usePanelList();
  const panelId = useSelector(PanelSelector);
  const businessChoice = useSelector(BusinessChoiceSelector);
  const isB2B = businessChoice === 'b2b';
  const dispatch = useDispatch();
  const lightImageRef = useRef<HTMLDivElement>(null);
  const numberInputRef = useRef<HTMLInputElement>(null);
  const {
    default: defaultConsumption,
    max,
    min,
    buttonStep: step,
    sliderStep,
    maxForLightAnimation,
  } = useConsumptionConfig();

  const userAngle = useSelector(RoofAngleSelector);
  const validAngle = getValidVideoAngle(userAngle);

  const videoUrl = useAsset(
    AssetType.VIDEO,
    validAngle === 0
      ? `/03-Conso/conso_flat.mp4`
      : `/03-Conso/conso_${roofType}_${validAngle}.mp4`
  );

  // NOTE: Even if it is an image, the asset prefix is video because it is related to the videos
  const lightImage = useAsset(AssetType.VIDEO, '/03-Conso/LIGHT.png');
  const [currentValue, setCurrentValue] = useState<number>(
    userConsumption || defaultConsumption
  );

  const ligthAmount = getLightAmountValue(
    currentValue,
    min,
    maxForLightAnimation
  );

  const shouldGoToMaxConsumptionPage = isB2B && currentValue === max;

  // --------------------- methods ------------------------

  const changeValue = useCallback(
    (value: number, isEnd: boolean = false) => {
      let val = value && !Number.isNaN(value) ? value : 0;
      val = val > max ? max : val;
      setCurrentValue(val);
      if (isEnd) dispatch(updateConsumption(val));
    },
    [dispatch, max]
  );

  const handleContinue = () => {
    dispatch(updateConsumption(currentValue));

    if (shouldGoToMaxConsumptionPage) {
      navigate(`${AppRoutes.SME_CONTACT}?contactReason=${SMEContactReason.KW}`);
      return;
    }
    navigateToNextStep();
  };

  const handleSkip = () => {
    dispatch(updateConsumption(undefined));
    navigate(AppRoutes.BILL_INFO);
  };

  const continueLabel = shouldGoToMaxConsumptionPage
    ? t('CONSUMPTION.b2b.max.continue', { amount: formatNumber(max) })
    : t('common.continue');

  // --------------------- effects ------------------------

  // initial fade
  useEffect(() => {
    if (lightImageRef.current) {
      lightImageRef.current.style.opacity = '0';
      gsap.to(lightImageRef.current, {
        duration: 1.7,
        delay: 0.7,
        autoAlpha: 1,
        startAt: { autoAlpha: 0 },
      });
    }
  }, [lightImageRef]);

  // --------------------- render ------------------------

  return (
    <PageLayout
      title={t('CONSUMPTION_title')}
      buttonLabel={continueLabel}
      buttonCallback={handleContinue}
      button={
        shouldGoToMaxConsumptionPage && (
          <Button
            variant="multilineWithArrow"
            maxW="300px"
            rightIcon={<ImArrowRight2 />}
            as={motion.button}
            initial={{ opacity: 0, translateX: -0, scale: 1.2 }}
            animate={{
              opacity: 1,
              scale: 1.0,
              translateX: 0,
              transition: { duration: 0.3, ease: 'anticipate' },
            }}
            exit={{ opacity: 0, translateX: 30, transition: { duration: 0.3 } }}
            onClick={handleContinue}
          >
            {t('CONSUMPTION.b2b.max.continue', { amount: formatNumber(max) })}
          </Button>
        )
      }
      backButton
      backCallback={navigateToPreviousStep}
    >
      <Box
        height={{ base: '180px', md: '250px' }}
        width="full"
        position="relative"
      >
        <VideoBlock
          loop={false}
          mt={
            isB2B
              ? { base: '-50px', md: '-60px' }
              : { base: '-50px', md: '-80px' }
          }
          position="relative"
          videoUrl={videoUrl}
          height={{ base: '300px', md: '400px' }}
          width={{ base: '300px', md: '400px' }}
          transform={isB2B ? { base: 'scale(1.5)', md: 'scale(1.7)' } : {}}
        >
          <div
            style={{
              width: '100%',
              height: '100%',
              position: 'absolute',
              opacity: 0,
            }}
            ref={lightImageRef}
          >
            <Image
              width="100%"
              height="100%"
              alt="light-intensity"
              src={lightImage}
              sx={{
                opacity: ligthAmount,
              }}
              position="absolute"
            />
          </div>
        </VideoBlock>
        <PanelIndicator
          amount={getEstimatedPanelsForConsumption(
            panelId,
            panelList,
            currentValue
          )}
          txt={t('common.panels.needed')}
          textPosition="left"
          right={isB2B ? { base: '0', md: '0px' } : { base: '0', md: '50px' }}
          top={isB2B ? { base: '0', md: '40px' } : { base: '0', md: '0' }}
          showAfter={3000}
        />
      </Box>

      <VStack w="100%">
        <VStack gap="0" position="relative" pb="10px">
          <InputNumber
            ref={numberInputRef}
            min={min}
            max={max}
            value={currentValue}
            onChange={(val) => changeValue(val, true)}

            // onClick={handleSelectAll}
          />
          <Text
            position="absolute"
            top="76px"
            color="primaryColor"
            fontSize={20}
            fontFamily="Primary Regular"
            bgColor="white"
            borderRadius="100%"
            px="5"
          >
            {t('CONSUMPTION_kwh')}
          </Text>
        </VStack>

        <HStack w="full">
          <IconButton
            aria-label="increase consumption"
            color="primaryColor"
            colorScheme="whiteAlpha"
            icon={<ImMinus />}
            onClick={() => {
              if (currentValue > step) {
                changeValue(currentValue - step, true);
              }
            }}
          />
          <RoundSlider
            min={min}
            max={max}
            baseValue={currentValue}
            step={sliderStep}
            onChange={(val) => changeValue(val)}
            onChangeEnd={(val) => changeValue(val, true)}
          />

          <IconButton
            aria-label="increase consumption"
            color="primaryColor"
            colorScheme="whiteAlpha"
            onClick={() => {
              changeValue(currentValue + step, true);
            }}
            icon={<ImPlus />}
          />
        </HStack>
        <Button variant="skip" onClick={handleSkip}>
          {t('CONSUMPTION_skip')}
        </Button>
      </VStack>
    </PageLayout>
  );
};

export default PageConsumption;
